import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classNames from 'classnames';

import i18n from '../../i18n';

import Conversation from '../../models/Conversation';
import Exchange from '../../models/Exchange';
import Transfert from '../../models/Transfert';
import User from '../../models/User';

import { SegmentedControl } from '@homeexchange/design';

import { MessagingV2Context } from './messagingv2/MessagingV2Context';
import { withCurrency } from '../currency';
import ExchangeEditable from './ExchangeEditable';
import ExchangeResume from './ExchangeResume';
import Loading from '../common/Loading';

import { getCouponByType } from '../../actions/subscription';

export class ExchangeTabs extends React.Component {
    uniqueId;

    static contextType = MessagingV2Context;

    static propTypes = {
        currency: PropTypes.string.isRequired,
        user: PropTypes.instanceOf(User).isRequired,
        editable: PropTypes.bool.isRequired,
        conversation: PropTypes.instanceOf(Conversation).isRequired,
        transferts: PropTypes.arrayOf(PropTypes.instanceOf(Transfert)),
        patchExchange: PropTypes.func,
        coupon: PropTypes.shape({
            couponName: PropTypes.string,
            discount: PropTypes.number
        }),
        getCoupon: PropTypes.func.isRequired
    };

    constructor(props, context) {
        super(props, context);

        this.exchangeTabsRef = React.createRef();
        this.tabContentRef = React.createRef();
        this.uniqueId = Math.random().toString(36).substr(2, 9);
        this.state = {
            selectedExchangeTab: props.conversation.getExchangeAsGuest() ? 'guest' : 'host'
        };
    }

    componentDidMount() {
        if (!this.context) {
            $(this.exchangeTabsRef.current).find('a:first').tab('show');
        }

        if (
            this.props.conversation.exchangeStartsInGivenDateRange(
                moment(Exchange.GP_PROMO_START_DATE),
                Exchange.GP_PROMO_NB_DAYS
            )
        ) {
            this.props.getCoupon('coupon_guestpoint_finalize_exchange');
        }
    }

    componentDidUpdate(prevProps) {
        const exchangesCountChanged =
            prevProps.conversation.get('exchanges').length !==
            this.props.conversation.get('exchanges').length;

        if (this.context) {
            if (exchangesCountChanged) {
                this.setState({
                    selectedExchangeTab: this.props.conversation.getExchangeAsGuest() ? 'guest' : 'host'
                });
            }
            return;
        }

        if (exchangesCountChanged || (!prevProps.transferts && this.props.transferts)) {
            $(this.exchangeTabsRef.current).find('a').tab('show').first().tab('show');
            return;
        }

        if (prevProps.editable !== this.props.editable) {
            this.restoreActiveTab();
        }
    }

    restoreActiveTab() {
        const tabs = $(this.exchangeTabsRef.current);
        const activeTab = tabs.find('li.active a');
        tabs.find('li').removeClass('active');
        (activeTab.length ? activeTab : tabs.find('a:first')).tab('show');
    }

    componentWillUnmount() {
        this._isUnmounted = true;
    }

    isHost() {
        return this.props.conversation.getExchange().get('host').id == this.props.user.id;
    }

    render() {
        // Wait for transferts to be loaded (transferts are needed for SubscriptionGPRow)
        if (!this.props.transferts) {
            return <Loading />;
        }
        if (this.context) {
            return (
                <div className="exchange-list exchange-list-v2">
                    {this.renderSegmentedControl()}
                    {this.renderPanelsV2()}
                </div>
            );
        }
        return (
            <div className="exchange-list exchange-list-legacy">
                {this.renderTabs()}
                {this.renderPanels()}
            </div>
        );
    }

    renderSegmentedControl() {
        if (!this.props.conversation.isMutual()) {
            return null;
        }

        const exchangeAsGuest = this.props.conversation.getExchangeAsGuest();
        const missingFieldsCount = exchangeAsGuest ? exchangeAsGuest.getMissingFields().length : 0;

        return (
            <SegmentedControl
                items={[
                    {
                        label:
                            i18n.t('exchange:your_exchange') +
                            (missingFieldsCount > 0 ? ` (${missingFieldsCount})` : ''),
                        value: 'guest'
                    },
                    {
                        label: i18n.t('exchange:user_exchange', {
                            first_name: this.props.conversation.get('interlocutor').get('first_name')
                        }),
                        value: 'host'
                    }
                ]}
                value={this.state.selectedExchangeTab}
                onChange={(selectedExchangeTab) => this.setState({ selectedExchangeTab })}
            />
        );
    }

    renderPanelsV2() {
        const { conversation } = this.props;
        const selectedType = conversation.isMutual()
            ? this.state.selectedExchangeTab
            : this.isHost()
            ? 'host'
            : 'guest';

        return (
            <div className="exchanges-panel-content">
                {conversation.get('exchanges').map((exchange) => {
                    const type = exchange.get('guest').id == this.props.user.id ? 'guest' : 'host';
                    return (
                        <div className="exchange-panel" hidden={type !== selectedType} key={type}>
                            {this.renderExchangeContent(exchange)}
                        </div>
                    );
                })}
            </div>
        );
    }

    renderTabs() {
        if (this.props.conversation.isMutual()) {
            return (
                <ul className="nav nav-tabs" role="tablist" ref={this.exchangeTabsRef}>
                    {this.renderGuestTab()}
                    {this.renderHostTab()}
                </ul>
            );
        } else {
            return (
                <ul className="nav nav-tabs" role="tablist" ref={this.exchangeTabsRef}>
                    {this.isHost() ? this.renderHostTab() : this.renderGuestTab()}
                </ul>
            );
        }
    }

    renderGuestTab() {
        const exchangeAsGuest = this.props.conversation.getExchangeAsGuest();
        const exchangeAsHost = this.props.conversation.getExchangeAsHost();
        if (!exchangeAsGuest && !this.props.editable) {
            return false;
        }
        return (
            <li
                role="presentation"
                className={classNames({ fake: !exchangeAsGuest, large: !exchangeAsHost })}
            >
                <a
                    href={`#exchange-guest-${this.uniqueId}`}
                    aria-controls="exchange-guest"
                    role="tab"
                    data-toggle="tab"
                    id="exchangeAsGuest"
                >
                    {i18n.t('exchange:your_exchange')}
                    &nbsp;
                    <span className="badge">
                        {exchangeAsGuest && exchangeAsGuest.getMissingFields().length > 0
                            ? exchangeAsGuest.getMissingFields().length
                            : ''}
                    </span>
                </a>
            </li>
        );
    }

    renderHostTab() {
        const exchangeAsHost = this.props.conversation.getExchangeAsHost();
        const exchangeAsGuest = this.props.conversation.getExchangeAsGuest();
        if (!exchangeAsHost && !this.props.editable) {
            return false;
        }
        return (
            <li
                role="presentation"
                className={classNames({ fake: !exchangeAsHost, large: !exchangeAsGuest })}
            >
                <a
                    href={`#exchange-host-${this.uniqueId}`}
                    aria-controls="exchange-host"
                    role="tab"
                    data-toggle="tab"
                    id="exchangeAsHost"
                >
                    {i18n.t('exchange:user_exchange', {
                        first_name: this.props.conversation.get('interlocutor').get('first_name')
                    })}
                </a>
            </li>
        );
    }

    renderExchangeContent(exchange) {
        if (this.props.editable) {
            return (
                <ExchangeEditable
                    currency={this.props.currency}
                    user={this.props.user}
                    conversation={this.props.conversation}
                    exchange={exchange}
                    transferts={this.props.transferts}
                    patchExchange={this.props.patchExchange}
                    coupon={this.props.coupon}
                />
            );
        }
        return (
            <ExchangeResume
                currency={this.props.currency}
                user={this.props.user}
                conversation={this.props.conversation}
                exchange={exchange}
                transferts={this.props.transferts}
            />
        );
    }

    renderPanels() {
        const { conversation } = this.props;

        const isMultipleTabs = conversation.get('exchanges').length === 2;
        const exchanges = conversation.get('exchanges').map((exchange) => {
            const type = exchange.get('guest').id == this.props.user.id ? 'guest' : 'host';
            return (
                <div
                    role="tabpanel"
                    className={classNames({
                        'exchange-panel': this.props.editable,
                        'tab-pane': isMultipleTabs || !this.props.editable
                    })}
                    id={`exchange-${type}-${this.uniqueId}`}
                    key={type}
                >
                    {this.renderExchangeContent(exchange)}
                </div>
            );
        });
        return (
            <div
                className={classNames('exchanges-panel-content', {
                    'tab-content': isMultipleTabs
                })}
                ref={this.tabContentRef}
            >
                {exchanges}
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        user: state.user,
        transferts: state.transferts,
        coupon: state.subscription && !_.isEmpty(state.subscription.coupon) ? state.subscription.coupon : null
    };
}

const mapDispatchToProps = (dispatch) => ({
    getCoupon: (coupon) => dispatch(getCouponByType(coupon))
});

export default compose(withCurrency, connect(mapStateToProps, mapDispatchToProps))(ExchangeTabs);
